import React, {useEffect,useState} from 'react';
import {connect} from 'react-redux';
import moment from 'moment-timezone';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import config from '../../config/config';
import {formatProviderName} from '../../helpers/providers';
import {
  getServiceCategories,
  getLocationTypes
} from '../../helpers/selectOptions';
import {
  useGetProvidersQuery
} from '../../redux/services/providerApi';
import ProviderAvatar from '../providers/ProviderAvatar';
import CustomBox from '../common/CustomBox';
import CustomChip from '../common/CustomChip';
import CustomDialog from '../common/CustomDialog';
import MatchProviderForm from './MatchProviderForm';

const BookingPreview = props => {
  const {
    provider,
    date,
    time,
    timeCreated,
    minutesRemaining,
    secondsRemaining,
    serviceCategory,
    locationType,
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const serviceCategoryLabel = getServiceCategories().find(sc => sc.value === serviceCategory)?.label;
  const locationTypeLabel = getLocationTypes().find(lt => lt.value === locationType)?.label;
  const secondsRemainingStr = secondsRemaining < 10 ? `0${secondsRemaining}` : secondsRemaining;
  return (
    <CustomBox>
      <Stack p={1}>
        <Stack direction='row' spacing={1} justifyContent='space-between' alignItems='center'>
          <Stack direction='row' spacing={1} alignItems='center'>
            <ProviderAvatar provider={provider} />
            <Stack gap={.25}>
              <Typography variant='h3'>
                {formatProviderName(provider)}
              </Typography>
              <Typography>
                {date && time
                  ? moment.tz(`${date} ${time}`, 'MM/DD/YYYY h:mm A', provider?.timezone || moment.tz.guess()).format('ddd MMM D [at] h:mm A')
                  : 'Exact Date of Session TBD'
                }
              </Typography>
            </Stack>
          </Stack>
          <Stack>
          {
            !!timeCreated && (secondsRemaining + minutesRemaining > 0) &&
              <Box 
                style={{
                  border: `1px solid ${config().colors.secondary}`,
                  padding: '3px', 
                  borderRadius: '5px',
                  minWidth: '95px',
                }}
              >
                <Stack direction='row' spacing={.5} justifyContent='space-between'>
                  <Typography variant='body2' color='secondary' p={0} m={0}>
                    Expires in 
                  </Typography>
                  <Typography variant='body2' color='secondary' p={0} m={0}>
                    {minutesRemaining}:{secondsRemainingStr}
                  </Typography>
                </Stack>
              </Box>
            }
            <Button size='small' onClick={() => setIsOpen(true)}>Edit</Button>
            </Stack>
        </Stack>
        <Stack spacing={.5} pt={1}>
          <Stack direction='row' gap={.25}>
            {!!serviceCategoryLabel && 
              <CustomChip label={serviceCategoryLabel} color='primary' variant='outlined'>
              </CustomChip>
            }
            {
              !!locationTypeLabel &&
              <CustomChip label={locationTypeLabel} color='primary' variant='outlined'>
              </CustomChip>
            }
          </Stack>
          {
              <Typography variant='body1' color='text.muted' pt={.25}>
              Please complete your account signup to {!!timeCreated ? 'secure your booking' : 'coordinate with the provider.'}
            </Typography>
          }
        </Stack>
      </Stack>
      <CustomDialog title='Edit Booking Details' open={isOpen} onClose={() => setIsOpen(false)}>
          <Container maxWidth='xs' sx={{p: 0}}>
            <MatchProviderForm 
              noPadding={true}
              omitTextSearchMethod={true}
              afterSubmit={({providerId}) => {
                setIsOpen(false);
              }}
            />
          </Container>
      </CustomDialog>
    </CustomBox>
  );
};

const BookingPreviewContainer = props => {
  const allottedTimeSeconds = 60 * 15; // 15 minutes
  const [timeElapsed, setTimeElapsed] = useState(
    moment().diff(moment(props.timeCreated), 'seconds')
  );
  // update time every 1 second
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeElapsed(moment().diff(moment(props.timeCreated), 'seconds'));
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const providerQuery = useGetProvidersQuery(
    {id: props.providerId},
    {skip: !props.providerId}
  );
  const childProps = {
    ...props,
    provider: providerQuery?.data?.[0],
    minutesRemaining: Math.max(0, Math.floor((allottedTimeSeconds - timeElapsed) / 60)),
    secondsRemaining: Math.max(0, (allottedTimeSeconds - timeElapsed) % 60),
    date: props.date,
    time: props.time,
    locationType: props.locationType,
    serviceCategory: props.serviceCategory,
  };
  if (!props?.providerId) return null;
  return <BookingPreview {...childProps} />;
};

const mapState = state => ({
  date: state?.newAppointmentForm?.date,
  time: state?.newAppointmentForm?.time,
  locationType: state?.newAppointmentForm?.locationType,
  providerId: state?.newAppointmentForm?.providerId,
  providerAvailabilityId: state?.newAppointmentForm?.providerAvailabilityId,
  scheduleManually: state?.newAppointmentForm?.scheduleManually,
  locationDetails: state?.locationDetails,
  timeCreated: state?.newAppointmentForm?.timeCreated,
  serviceCategory: state?.newAppointmentForm?.serviceType,
});

const mapDispatch = dispatch => ({

});

export default connect(mapState, mapDispatch)(BookingPreviewContainer);